import React, { useCallback, useMemo, useId } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import {
  VisibilityOutlined as ViewIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { BaseTable, TableColumn } from '../../common/Table';
import { StatusChip, ActionButton } from '../../common/Table/Table.styles';
import type { ChipColor } from '../../common/Table/Table.hooks';
import {
  catalogActionIconButtonSx,
  catalogActionSvgIconGradientSx,
} from '../../products/catalogGradients';
import { CatalogActionIconGradientDefs } from '../../products/CatalogActionIconGradientDefs';
import type { ClientManagementRow } from './types';
import { formatPhoneNumber } from '../../../utils/formatters/text';

const formatMoney = (value: number): string =>
  new Intl.NumberFormat('es-BO', {
    style: 'currency',
    currency: 'BOB',
    maximumFractionDigits: 2,
  }).format(value);

const tierLabel = (tier: ClientManagementRow['loyaltyTier']): string => {
  const map: Record<ClientManagementRow['loyaltyTier'], string> = {
    BRONZE: 'Bronce',
    SILVER: 'Plata',
    GOLD: 'Oro',
    PLATINUM: 'Platino',
  };
  return map[tier];
};

const tierChipColor = (tier: ClientManagementRow['loyaltyTier']): ChipColor => {
  if (tier === 'PLATINUM') return 'secondary';
  if (tier === 'GOLD') return 'warning';
  if (tier === 'SILVER') return 'info';
  return 'default';
};

export interface ClientManagementTableProps {
  clients: ClientManagementRow[];
  selectedClients: number[];
  setSelectedClients: React.Dispatch<React.SetStateAction<number[]>>;
  onViewClient: (id: number) => void;
  onEditClient: (id: number) => void;
  onDeleteClient: (id: number) => void;
}

const ClientManagementTable: React.FC<ClientManagementTableProps> = ({
  clients,
  selectedClients,
  setSelectedClients,
  onViewClient,
  onEditClient,
  onDeleteClient,
}) => {
  const columns: TableColumn<ClientManagementRow>[] = useMemo(
    () => [
      {
        id: 'customerName',
        label: 'Nombre',
        sortAccessor: (item) => item.customerName,
        render: (item) => (
          <Typography color="text.primary" fontWeight={600} fontSize="1rem">
            {item.customerName}
          </Typography>
        ),
      },
      {
        id: 'identityDocumentNumber',
        label: 'CI',
        sortAccessor: (item) => item.identityDocumentNumber,
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.identityDocumentNumber?.trim() || '—'}
          </Typography>
        ),
      },
      {
        id: 'customerEmail',
        label: 'Correo',
        sortAccessor: (item) => item.customerEmail,
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.customerEmail ?? '—'}
          </Typography>
        ),
      },
      {
        id: 'customerPhone',
        label: 'Teléfono',
        sortAccessor: (item) => item.customerPhone,
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.customerPhone != null && item.customerPhone.trim() !== ''
              ? formatPhoneNumber(item.customerPhone)
              : '—'}
          </Typography>
        ),
      },
      {
        id: 'referenceNumbers',
        label: 'Nº referencia',
        sortAccessor: (item) =>
          [item.referenceNumber1, item.referenceNumber2].filter(Boolean).join(' '),
        render: (item) => {
          const r1 = item.referenceNumber1?.trim();
          const r2 = item.referenceNumber2?.trim();
          if (!r1 && !r2) {
            return (
              <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
                —
              </Typography>
            );
          }
          return (
            <Box>
              {r1 ? (
                <Typography color="text.secondary" fontWeight={500} fontSize="0.9rem">
                  {r1}
                </Typography>
              ) : null}
              {r2 ? (
                <Typography color="text.secondary" fontWeight={500} fontSize="0.9rem">
                  {r2}
                </Typography>
              ) : null}
            </Box>
          );
        },
      },
      {
        id: 'loyaltyTier',
        label: 'Nivel de lealtad',
        sortAccessor: (item) => item.loyaltyTier,
        render: (item) => (
          <StatusChip
            label={tierLabel(item.loyaltyTier)}
            size="small"
            color={tierChipColor(item.loyaltyTier)}
          />
        ),
      },
      {
        id: 'totalPurchases',
        label: 'Total compras',
        sortAccessor: (item) => item.totalPurchases,
        render: (item) => (
          <Typography color="success.main" fontWeight={600} fontSize="1rem">
            {formatMoney(item.totalPurchases)}
          </Typography>
        ),
      },
      {
        id: 'purchaseCount',
        label: 'Nº compras',
        sortAccessor: (item) => item.purchaseCount,
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.purchaseCount}
          </Typography>
        ),
      },
    ],
    [],
  );

  const handleSelectItems = useCallback(
    (items: (string | number)[]) => setSelectedClients(items as number[]),
    [setSelectedClients],
  );

  const catalogIconGradientId = `crm-client-action-grad-${useId().replace(/:/g, '')}`;
  const iconGradSx = catalogActionSvgIconGradientSx(catalogIconGradientId);

  return (
    <>
      <CatalogActionIconGradientDefs id={catalogIconGradientId} />
      <BaseTable<ClientManagementRow>
        items={clients}
        columns={columns}
        selectedItems={selectedClients}
        onSelectItems={handleSelectItems}
        showCheckbox
        showActions
        getItemId={(item) => item.id}
        emptyStateTitle="No se encontraron clientes"
        renderActions={(row) => (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Tooltip title="Ver detalles" arrow>
              <ActionButton
                size="small"
                onClick={() => onViewClient(row.id)}
                sx={catalogActionIconButtonSx}
              >
                <ViewIcon fontSize="small" sx={iconGradSx} />
              </ActionButton>
            </Tooltip>
            <Tooltip title="Editar" arrow>
              <ActionButton
                size="small"
                onClick={() => onEditClient(row.id)}
                sx={catalogActionIconButtonSx}
              >
                <EditIcon fontSize="small" sx={iconGradSx} />
              </ActionButton>
            </Tooltip>
            <Tooltip title="Eliminar" arrow>
              <ActionButton
                size="small"
                onClick={() => onDeleteClient(row.id)}
                sx={catalogActionIconButtonSx}
              >
                <DeleteIcon fontSize="small" sx={iconGradSx} />
              </ActionButton>
            </Tooltip>
          </Box>
        )}
      />
    </>
  );
};

export default ClientManagementTable;
